import videojs from 'video.js';

import 'video.js/dist/video-js.css';
import PausedOverlay from './PausedOverlay';

class VideoPlayerDispenserHelper {
    constructor() {
        this.BLANK_VIDEO_PATH = 'assets/videos/blank.mp4';
        this.BLANK_IMAGE_PATH = 'assets/videos/blank.png';

        /** @type {Object.<string, string>} */
        this.videoPlayerIds = {
            mainVideoPlayer: 'mainVideoPlayer',
            // -- add the video players used by the game here (video tag id), each video player will also need a DOM element in 'template.html' --
        };

        /** @type {Object.<string, {player: videojs.Player, wrapper: HTMLElement}>} */
        this.videoPlayerInstances = {};

        this._pausedOverlay = new PausedOverlay(document.getElementById('pausedOverlay'), document.getElementById('resumeButton'));
    }

    /**
     * Gets a video player by id
     * @return {videojs.Player} The video player.
     */
    getVideoPlayer(id) {
        return this.videoPlayerInstances[id].player;
    }

    /**
     * Gets the wrapper by video player id
     * @return {HTMLDivElement} The wrapper.
     */
    getVideoWrapper(id) {
        return this.videoPlayerInstances[id].wrapper;
    }

    /**
     * Gets the overlay that is shown when the video is paused
     * @return {PausedOverlay} The overlay.
     */
    getPausedOverlay() {
        return this._pausedOverlay;
    }

    /**
     * Initializes video players. Should be called as soon as the web app starts.
     * @return {Promise<void>} A promise that will be resolved when the video players are ready.
     */
    initialize() {
        if (this._initializeWasCalled) {
            console.warn(`[VideoPlayerDispenserHelper] VideoPlayerDispenserHelper.initialize() was called more than once. Ignoring request...`);
            return;
        }

        this._initializeWasCalled = true;

        // Map video players to initialization promises.
        const videoPlayerInitializationPromises = Object.values(this.videoPlayerIds)
            .map(videoPlayerId => {
                return this._initializeVideoPlayerInstance(videoPlayerId)
                    .then(({ videoPlayerInstance }) => {
                        // Assign a DOM wrapper to initialized video player instance and keep it for later.
                        this.videoPlayerInstances[videoPlayerId] = {
                            player: videoPlayerInstance,
                            wrapper: this._getDOMElementForVideoPlayerId(videoPlayerId),
                        };

                        console.log(`[VideoPlayerDispenserHelper] Successfully initialized video player '${videoPlayerId}'.`);
                    });
            });

        return Promise.all(videoPlayerInitializationPromises)
            .then(() => {
                // Once all video players are initialized and ready for playback we'll use the next user interaction (click) to enable autoplay for all video players.
                document.addEventListener("click", () => {
                    this.initializeAutoplay();
                }, { once: true });
            });
    }

    /**
     * Loads and plays the blank video in order to prepare the video players for autoplay on the mobile devices.
     * Must be called by a user interaction (e.g., mouse click).
     * This means that the beginning of the call stack for this call should have a user interaction.
     * @return {void}
     */
    initializeAutoplay() {
        for (var key in this.videoPlayerIds) {
            const videoPlayerId = this.videoPlayerIds[key];
            const playerInstance = this.getVideoPlayer(videoPlayerId);

            playerInstance.src(this.BLANK_VIDEO_PATH);
            playerInstance.poster(this.BLANK_IMAGE_PATH);

            playerInstance.play();
        }

        console.log(`[VideoPlayerDispenserHelper] Autoplay initialized for the following video players:`, Object.keys(this.videoPlayerIds));
    }

    _initializeVideoPlayerInstance(videoPlayerId) {
        /** @type {videojs.Player} */
        let videoPlayerInstance;
        return new Promise(resolve => {
            videoPlayerInstance = videojs(videoPlayerId, {
                controls: false,
                src: this.BLANK_VIDEO_PATH,
                html5: {
                    nativeTextTracks: false
                }
                // leave `fluid` to false, responsiveness is handled in css with the 'ranj-video-cover' class
            }, () => resolve({ videoPlayerInstance, videoPlayerId })); // resolve when the video player is ready to be used
        });
    }

    _getDOMElementForVideoPlayerId(videoPlayerId) {
        return document.getElementById(`${videoPlayerId}Wrapper`);
    }
};

const instance = new VideoPlayerDispenserHelper();
export default instance;